import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  ButtonBase,
  IconButton,
  Button,
  Stack,
  Menu,
  MenuItem,
  Tooltip,
  InputBase,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import OnlyOneTapLogo from "../../assets/OnlyOneTapLogo.svg";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useLocation as useAppLocation } from "../../context/NewLocationContext";
import { useTranslation } from "react-i18next";
import { homePathFor, useAuth } from "../../context/AuthContext";
import PopoverMenu from "../popover/Popover";
import MobileAppBanner, { MOBILE_BANNER_HEIGHT } from "../common/MobileAppBanner";
import LanguageDialog from "../common/language/LanguageDialog";
import LangSwapIcon from "../common/LangSwapIcon";
import UserAvatar from "../common/UserAvatar";

/**
 * OOTWUI-024: "Find an account" box for admins and field operators (desktop only, English only).
 * Opens Account Management with the phone in router state (never in the URL) on the 10th digit or Enter.
 * Also shown inside the Account Management page.
 */
export const FindAccountBox: React.FC<{ accountManagementPath: string; showClear?: boolean; sx?: SxProps<Theme> }> = ({
  accountManagementPath,
  showClear = false,
  sx,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [phone, setPhone] = useState("");
  // The number just searched: Enter right after the 10th digit must not search (and add a history entry) twice.
  const [lastSearched, setLastSearched] = useState("");
  const search = (value: string) => {
    if (value.length !== 10 || value === lastSearched) return;
    setLastSearched(value);
    navigate(accountManagementPath, { state: { phone: value } });
  };
  // Clear empties the number and, on Account Management, the selected account too.
  const clear = () => {
    setPhone("");
    setLastSearched("");
    if (location.pathname === accountManagementPath) navigate(accountManagementPath, { state: { clear: true } });
  };

  return (
    <Box
      title="Find an account by mobile number"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        width: { md: 180, lg: 220, xl: 260 },
        height: 40,
        px: 1.5,
        borderRadius: "20px",
        border: "1px solid",
        borderColor: "primary.main",
        bgcolor: "background.paper",
        ...(sx as object),
      }}
    >
      <SearchIcon sx={{ fontSize: 16, color: "primary.main" }} />
      <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "text.primary" }}>+91</Typography>
      <InputBase
        value={phone}
        placeholder="Find an account"
        inputProps={{ "aria-label": "Find an account", inputMode: "numeric" }}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
          setPhone(digits);
          if (digits.length < 10) setLastSearched("");
          search(digits);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") search(phone);
        }}
        sx={{ flex: 1, minWidth: 0, fontSize: "14px" }}
      />
      {(phone || showClear) && (
        <IconButton size="small" aria-label="Clear" onClick={clear} sx={{ p: 0.25, color: "text.secondary", minWidth: { xs: 44, md: 0 }, minHeight: { xs: 44, md: 0 } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();
  const { displayCity, displayLocality, openLocationDialog } = useAppLocation();

  // Auth-flow pages (login/signup/otp/passcode/forgot/provider-registration)
  // never show Login/Sign up in the brand bar — you're already there. Only
  // the language switcher stays. Frozen-header rule; mobile only, matches
  // the design mockups' Login/Signup screens exactly.
  const isAuthFlowRoute = ["/login", "/register", "/forgot", "/service-provider/register"].some(
    (prefix) => location.pathname.startsWith(prefix)
  );

  const isAdmin = user?.userType === "INTERNAL_USER";
  const isFieldOperator = user?.userType === "FIELD_OPERATOR";

  const navLinks = [
    { label: t("header.navHome"), path: homePathFor(user?.userType) },
    { label: t("header.navAllServices"), path: "/all-service-categories" },
    { label: t("header.navAllCities"), path: "/all-cities" },
    { label: t("header.navHowItWorks"), path: "/how-it-works" },
    ...(!isAuthenticated ? [{ label: t("header.navBecomeProvider"), path: "/service-provider/register" }] : []),
  ];

  const [langOpen, setLangOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [signupAnchorEl, setSignupAnchorEl] = useState<HTMLElement | null>(null);

  const toggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const closeMenu = () => setAnchorEl(null);

  const toggleSignupMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSignupAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const closeSignupMenu = () => setSignupAnchorEl(null);

  const handleSignupNavigation = (path: string) => {
    navigate(path);
    closeSignupMenu();
  };

  const open = Boolean(anchorEl);
  const signupOpen = Boolean(signupAnchorEl);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem("mobileAppBannerClosed") === "true"
  );

  const bannerVisible = isSmallScreen && !bannerDismissed;

  // Only track the boolean flip (sticky → fixed), not every pixel.
  // CSS variable handles the smooth per-pixel offset for sub-headers.
  const [scrolledPastBanner, setScrolledPastBanner] = useState(
    () => !bannerVisible || window.scrollY >= MOBILE_BANNER_HEIGHT
  );

  useEffect(() => {
    if (!bannerVisible) {
      setScrolledPastBanner(true);
      return;
    }

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolledPastBanner(window.scrollY >= MOBILE_BANNER_HEIGHT);
        ticking = false;
      });
    };

    setScrolledPastBanner(window.scrollY >= MOBILE_BANNER_HEIGHT);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [bannerVisible]);

  const appBarPosition: "fixed" | "sticky" =
    isSmallScreen && bannerVisible && !scrolledPastBanner ? "sticky" : "fixed";
  const appBarTop = appBarPosition === "fixed" ? 0 : undefined;

  const handleBannerClose = () => {
    setBannerDismissed(true);
    setScrolledPastBanner(true);
    // Notify useAppBarTop (which lives in page components) so it can
    // immediately clear the banner offset from --appbar-top without
    // waiting for a React re-render of the page subtree.
    window.dispatchEvent(new Event("mobileBannerDismissed"));
  };

  return (
    <>
      <MobileAppBanner onClose={handleBannerClose} />
      <AppBar
        position={appBarPosition}
        color="inherit"
        elevation={0}
        sx={{
          top: appBarTop,
          // Mobile: brand teal gradient. Desktop: white
          // sticky nav bar with a hairline border, matching DesktopHome.jsx.
          background: isSmallScreen
            ? `linear-gradient(100deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 17%, #189a90 48%, ${theme.palette.primary.dark} 115%)`
            : theme.palette.background.paper,
          borderBottom: isSmallScreen ? "none" : "1px solid",
          borderColor: "divider",
          boxShadow: isSmallScreen ? "none" : "0 1px 10px rgba(0,0,0,.04)",
        }}
      >
        <Toolbar
          sx={{
            maxWidth: "lg",
            width: "100%",
            mx: "auto",
            ...(isAuthenticated && isSmallScreen
              ? { minHeight: "45px !important", height: "45px" }
              : {}),
            ...(!isSmallScreen && {
              minHeight: "72px !important",
              height: "72px",
              gap: { md: 1.5, lg: 3, xl: 4 },
              px: { md: 1.5, lg: 2, xl: 3 },
            }),
          }}
        >
          <ButtonBase onClick={() => navigate(homePathFor(user?.userType))}>
            <Box
              component="img"
              src={OnlyOneTapLogo}
              alt="OnlyOneTap logo"
              height={isSmallScreen ? 42 : 48}
              width={isSmallScreen ? 42 : 48}
              sx={{ aspectRatio: "1 / 1", display: "block" }}
            />
          </ButtonBase>

          {isSmallScreen ? (
            // Mobile: brand name + nav links (All Services, All Cities) inline
            <>
              <Typography
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700,
                  color: "information.main",
                  flexGrow: 1,
                  textAlign: "left",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "14px",
                  ml: 1,
                  "@media (max-width:320px)": { fontSize: "10px" },
                }}
              >
                {t("header.title")}
              </Typography>
              {/* Show nav links only on sm and md — hidden on xs */}
              <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.5, ml: 0.5, flexShrink: 0 }}>
                <Button
                  onClick={() => navigate("/all-service-categories")}
                  sx={{
                    fontSize: "10px",
                    fontWeight: location.pathname === "/all-service-categories" ? 700 : 500,
                    color: location.pathname === "/all-service-categories" ? "secondary.main" : "information.main",
                    textTransform: "none",
                    minWidth: "auto",
                    px: "6px",
                    py: "2px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("header.navAllServices")}
                </Button>
                <Button
                  onClick={() => navigate("/all-cities")}
                  sx={{
                    fontSize: "10px",
                    fontWeight: location.pathname === "/all-cities" ? 700 : 500,
                    color: location.pathname === "/all-cities" ? "secondary.main" : "information.main",
                    textTransform: "none",
                    minWidth: "auto",
                    px: "6px",
                    py: "2px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("header.navAllCities")}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  color: "primary.dark",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  fontSize: { md: "14px", lg: "16px", xl: "18px" },
                  letterSpacing: { md: "0.6px", lg: "1px", xl: "1.2px" },
                  ml: { md: 0.5, lg: 1, xl: 1.25 },
                }}
              >
                {t("header.title")}
              </Typography>

              <Stack direction="row" spacing={{ md: 2, lg: 3, xl: 3.5 }} sx={{ ml: { md: 1.5, lg: 3, xl: 4 } }}>
                {navLinks.map((link) => (
                  <Typography
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    sx={{
                      fontSize: { md: "13px", lg: "14px", xl: "14.5px" },
                      fontWeight: location.pathname === link.path ? 700 : 500,
                      color: location.pathname === link.path ? "primary.main" : "text.secondary",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>

              <Box sx={{ flexGrow: 1 }} />
            </>
          )}

          {isSmallScreen ? (
            isAuthenticated ? (
              // Mobile authenticated — keep existing auth buttons; nav links above handle navigation
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={t("language.title")} placement="bottom">
                  <IconButton
                    onClick={() => setLangOpen(true)}
                    sx={{ color: "secondary.main" }}
                  >
                    <LangSwapIcon size={22} />
                  </IconButton>
                </Tooltip>
                <ButtonBase
                  onClick={() => navigate("/account/edit")}
                  sx={{ borderRadius: "50%", ml: 0.5 }}
                  aria-label={t("sidebar.myAccount")}
                >
                  <UserAvatar user={user} sx={{ width: 34, height: 34, fontSize: "14px" }} />
                </ButtonBase>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                <Tooltip title={t("language.title")} placement="bottom">
                  <IconButton
                    onClick={() => setLangOpen(true)}
                    sx={{ color: "secondary.main", p: "4px" }}
                  >
                    <LangSwapIcon size={22} />
                  </IconButton>
                </Tooltip>
                {!isAuthFlowRoute && (
                  <>
                    <Button
                      onClick={() => navigate("/login")}
                      sx={{
                        fontSize: "12px",
                        "@media (max-width:320px)": { fontSize: "10px" },
                        fontWeight: 500,
                        color: "information.main",
                        textTransform: "none",
                        minWidth: "auto",
                        px: { xs: "6px", sm: "8px" },
                        py: 0,
                      }}
                    >
                      {t("header.login")}
                    </Button>

                    <Button
                      onClick={toggleSignupMenu}
                      endIcon={signupOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                      sx={{
                        fontSize: "12px",
                        "@media (max-width:320px)": { fontSize: "10px" },
                        fontWeight: 700,
                        color: "primary.dark",
                        bgcolor: "secondary.main",
                        borderRadius: "16px",
                        textTransform: "none",
                        minWidth: "auto",
                        px: { xs: "10px", sm: "12px" },
                        py: "4px",
                        "&:hover": { bgcolor: "secondary.main" },
                        "& .MuiButton-endIcon": {
                          marginLeft: "2px",
                        },
                      }}
                    >
                      {t("header.signup")}
                    </Button>

                    <Menu
                      anchorEl={signupAnchorEl}
                      open={signupOpen}
                      onClose={closeSignupMenu}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <MenuItem
                        onClick={() => handleSignupNavigation("/register")}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "text.primary",
                          gap: 1,
                        }}
                      >
                        <PersonOutlineIcon sx={{ fontSize: 18, color: "primary.main" }} />
                        {t("header.signupAsUser")}
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleSignupNavigation("/service-provider/register")}
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "primary.main",
                          gap: 1,
                        }}
                      >
                        <BusinessCenterIcon sx={{ fontSize: 18, color: "primary.main" }} />
                        {t("header.registerAsProvider")}
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Stack>
            )
          ) : (
            <Stack direction="row" spacing={{ md: 0.75, lg: 1.25, xl: 1.5 }} alignItems="center">
              <Box
                onClick={() => openLocationDialog("header")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: { md: 1, lg: 1.25, xl: 1.5 },
                  py: 0.75,
                  borderRadius: "22px",
                  bgcolor: "grey.100",
                  border: "1px solid",
                  borderColor: "divider",
                  cursor: "pointer",
                  maxWidth: { md: 130, lg: 160, xl: 200 },
                  overflow: "hidden",
                }}
              >
                <LocationOnIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
                <Typography sx={{
                  fontSize: { md: "12px", lg: "13px", xl: "13.5px" },
                  fontWeight: 600,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {displayLocality || displayCity || t("home.selectLocation")}
                </Typography>
                <KeyboardArrowDownIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
              </Box>

              {/* Help page link for everyone (a real href, so it's crawlable) */}
              <Button
                component={RouterLink}
                to="/content/help"
                sx={{
                  fontSize: { md: "13px", xl: "14px" },
                  fontWeight: 600,
                  color: "text.primary",
                  textTransform: "none",
                  minWidth: "auto",
                  px: { md: 0.75, xl: 1 },
                  whiteSpace: "nowrap",
                }}
              >
                {t("helpCenter.needHelp")}
              </Button>

              <Tooltip title={t("language.title")} placement="bottom">
                <IconButton onClick={() => setLangOpen(true)} sx={{ color: "text.secondary", p: { md: "6px", xl: "8px" } }}>
                  <LangSwapIcon size={22} />
                </IconButton>
              </Tooltip>

              {isAuthenticated && (isAdmin || isFieldOperator) && (
                <FindAccountBox
                  accountManagementPath={isAdmin ? "/admin/account-management" : "/field-operator/account-management"}
                />
              )}

              {isAuthenticated ? (
                <ButtonBase
                  onClick={toggleMenu}
                  sx={{ borderRadius: "50%" }}
                  aria-label={t("sidebar.myAccount")}
                >
                  <UserAvatar user={user} sx={{ width: 38, height: 38, fontSize: "15px" }} />
                </ButtonBase>
              ) : (
                !isAuthFlowRoute && (
                  <>
                    <Typography
                      onClick={() => navigate("/login")}
                      sx={{ fontSize: { md: "13px", xl: "14px" }, fontWeight: 600, color: "primary.dark", cursor: "pointer", px: { md: 0.25, xl: 0.75 }, whiteSpace: "nowrap" }}
                    >
                      {t("header.login")}
                    </Typography>
                    <Button
                      onClick={toggleSignupMenu}
                      endIcon={signupOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                      sx={{
                        fontSize: { md: "13px", xl: "14px" },
                        fontWeight: 700,
                        color: "primary.dark",
                        bgcolor: "secondary.main",
                        borderRadius: "22px",
                        textTransform: "none",
                        px: { md: 1.5, xl: 2.25 },
                        py: "9px",
                        whiteSpace: "nowrap",
                        "&:hover": { bgcolor: "secondary.main" },
                      }}
                    >
                      {t("header.signup")}
                    </Button>
                    <Menu
                      anchorEl={signupAnchorEl}
                      open={signupOpen}
                      onClose={closeSignupMenu}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => handleSignupNavigation("/register")}
                        sx={{ fontSize: "13px", fontWeight: 500, color: "text.primary", gap: 1 }}
                      >
                        <PersonOutlineIcon sx={{ fontSize: 18, color: "primary.main" }} />
                        {t("header.signupAsUser")}
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleSignupNavigation("/service-provider/register")}
                        sx={{ fontSize: "13px", fontWeight: 500, color: "primary.main", gap: 1 }}
                      >
                        <BusinessCenterIcon sx={{ fontSize: 18, color: "primary.main" }} />
                        {t("header.registerAsProvider")}
                      </MenuItem>
                    </Menu>
                  </>
                )
              )}
            </Stack>
          )}

          <PopoverMenu
            anchorEl={anchorEl}
            open={open}
            onClose={closeMenu}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            isFieldOperator={isFieldOperator}
            logout={logout}
          />

          <LanguageDialog open={langOpen} onClose={() => setLangOpen(false)} />
        </Toolbar>
      </AppBar>

      {/* Spacer only needed when AppBar is fixed (sticky AppBar is in normal flow) */}
      {appBarPosition === "fixed" && (
        <Toolbar
          sx={{
            ...(isAuthenticated && isSmallScreen
              ? { minHeight: "45px !important", height: "45px" }
              : {}),
            ...(!isSmallScreen && { minHeight: "72px !important", height: "72px" }),
          }}
        />
      )}
    </>
  );
};

export default Header;
